import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const courseCards = [
  {
    name: '习近平新时代中国特色社会主义思想概论',
    highlight: '理论、政策、现实案例和价值目标之间能否形成完整联系',
    color: 'primary' as const,
  },
  {
    name: '马克思主义基本原理',
    highlight: '概念是否准确、论证是否展开、原理能否用于解释现实',
    color: 'accent' as const,
  },
  {
    name: '中国近现代史纲要',
    highlight: '史实准确、历史线索、因果关系和历史评价',
    color: 'primary' as const,
  },
  {
    name: '思想道德与法治',
    highlight: '学生现实问题、规范判断和行为引导',
    color: 'accent' as const,
  },
  {
    name: '毛泽东思想和中国特色社会主义理论体系概论',
    highlight: '理论体系、历史逻辑和现实发展之间的联系',
    color: 'primary' as const,
  },
  {
    name: '形势与政策',
    highlight: '时事把握、政策解读和现实问题分析',
    color: 'accent' as const,
  },
];

export default function P68WrapUp({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-4 md:py-5">
        <div ref={titleRef} className="text-center mb-3 md:mb-4">
          <span className={`inline-flex items-center gap-2 text-lg font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-3 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />共同底座 + 课程模型
          </span>
          <h2 className={`font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-3 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            同一套技术底座，不同的<span className="text-gold-600">课程判断重点</span>
          </h2>
          <p className={`text-lg text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            一套平台可以承载多门课程，但不能用一把尺子替代所有课程的专业判断
          </p>
        </div>

        {/* Lower layer: course cards */}
        <div className={`max-w-6xl mx-auto mb-3 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="text-center mb-2">
            <span className="inline-block px-3 py-1 rounded-full text-base font-body font-bold bg-primary-100/60 text-primary-700 border border-primary-200/40">
              课程专属评价重点
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2.5 md:gap-3">
            {courseCards.map((c, i) => (
              <div
                key={c.name}
                className={`p-4 rounded-xl border text-center transition-all duration-300 hover:scale-[1.02] ${
                  c.color === 'accent'
                    ? 'bg-gold-100/60 border-gold-200/50'
                    : 'bg-primary-100/30 border-primary-200/40'
                }`}
                style={{ transitionDelay: `${300 + i * 80}ms` }}
              >
                <h4 className={`text-base font-body font-bold mb-1.5 leading-tight ${c.color === 'accent' ? 'text-gold-700' : 'text-primary-700'}`}>
                  {c.name}
                </h4>
                <p className="text-sm text-foreground-950 font-body leading-relaxed">{c.highlight}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Center conclusion */}
        <div className={`max-w-3xl mx-auto mb-2 transition-all duration-1000 delay-600 ${titleVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <div className="p-4 rounded-xl glass-gold border border-gold-200/40 text-center">
            <p className="font-heading text-xl md:text-2xl font-bold text-gold-800">
              技术能力可以共享，评价标准必须分课程建设
            </p>
          </div>
        </div>

        <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-4 glass-liquid rounded-xl">
            <p className="text-lg text-foreground-950 font-body leading-relaxed">
              真正的跨课程AI评课，需要为每门课程分别建设<strong className="text-gold-700">教材知识库、课程知识图谱、专属评价点、专家样本和校准数据</strong>。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}